import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Camera, Phone } from 'lucide-react';
type Job = {
  src: string;
  alt: string;
  title: string;
  category: string;
  description: string;
};
const jobs: Job[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/2XnqEJUmz9MAdapqtYF9YY/josejunk3.png',
  alt: "Jose's crew loading construction debris into the truck",
  title: 'Property Cleanup',
  category: 'Construction Debris',
  description: 'Full property cleanup — loading wood, piping, and structural debris from a rural Monterey job site.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/5Q9Z9PcMTpdeX7kn3nPbwt/josejunk4.png',
  alt: "Jose's team cleaning up fire-damaged shed",
  title: 'Fire Damage Cleanout',
  category: 'Demolition & Removal',
  description: 'Post-fire shed cleanup. Crew bagged charred debris and hauled the wreckage so the homeowner could rebuild.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6WreFCYK53bmy2xKp5N3Az/josejunk2.png',
  alt: "Jose's truck dumping brick and concrete debris at disposal site",
  title: 'Concrete & Brick Haul',
  category: 'Heavy Material Disposal',
  description: 'Full load of concrete, brick, and stone hauled from a demo job and disposed of at the proper facility.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9PDGe3YDPBfjfgtNT8WSBZ/josejunk5.png',
  alt: "Jose's branded truck unloading rubble at dump",
  title: 'Construction Clean-Up',
  category: 'Job Site Haul',
  description: 'Full job site clearing — rubble, dirt, and demolition waste hauled in a single trip. One call. Everything gone.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/xd6QPCHM6CuKQ8Qr339PEi/josejunk7.png',
  alt: "Jose's crew loading old wood and fencing into the truck at a residential property",
  title: 'Residential Yard Cleanup',
  category: 'Wood & Fence Removal',
  description: 'Hauled old wood, fencing, and yard debris from a Monterey home. Driveway left spotless and ready for the next project.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/3MUhUAd5JbPHZeWzBy14iy/josejunk6.png',
  alt: "Jose's truck loaded with debris at a remote rural site",
  title: 'Remote Site Removal',
  category: 'Off-Road Hauling',
  description: 'When the job is off the beaten path, we still show up. Hauled scrap wood and debris from a rural property — no site too tough.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/wTXmKyUwPhJXk3kNKVCBBT/josejunk8.png',
  alt: "Commercial cleanout — Jose's crew clearing out a retail interior during remodel",
  title: 'Commercial Cleanout',
  category: 'Retail & Office',
  description: 'Interior remodel cleanout — fixtures, fittings, and demo debris cleared so the contractors could move in without delay.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/eJaVuqQNhGQPtABFdeyWnX/junkjremoval8.png',
  alt: "Jose's truck delivering a pile of fresh mulch at a property",
  title: 'Mulch Delivery',
  category: 'Hauling & Deliveries',
  description: "We don't just haul away — we deliver too. Fresh mulch dropped on-site for a landscaping project. Need it moved? We handle that."
}];
export function Gallery() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const goTo = useCallback((newIndex: number) => {
    setDirection(newIndex > index ? 1 : -1);
    setIndex((newIndex + jobs.length) % jobs.length);
  }, [index]);
  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);
  // Auto-advance
  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);
  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);
  const current = jobs[index];
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? '-100%' : '100%',
      opacity: 0
    })
  };
  return <section id="gallery" className="py-20 lg:py-28 bg-[#0E0E0E] text-white relative overflow-hidden" data-id="element-229" data-mp="zgsrr8w3zbdx">
      {/* Backdrop accents */}
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-[#C8102E] opacity-[0.08] blur-[160px] rounded-full pointer-events-none" data-id="element-230" data-mp="vdnrze3exnsz" />
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#C8102E] opacity-[0.05] blur-[160px] rounded-full pointer-events-none" data-id="element-231" data-mp="3g5g6z3yzgj44" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-232" data-mp="3b5k2z4qacrok">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10" data-id="element-233" data-mp="1qplxve7fgrun">
          <div data-id="element-234" data-mp="1vo701j68pllq">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-5" data-id="element-235" data-mp="1vnxbm00ufmi5">
              <Camera size={14} className="text-[#C8102E]" data-id="element-236" data-mp="2jczxjk2ig1ig" />
              <span className="text-[11px] tracking-[0.25em] font-bold uppercase text-gray-300" data-id="element-237" data-mp="2jdhvqm8qgpt0">
                On The Job
              </span>
            </motion.div>

            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-4xl md:text-5xl font-bold leading-tight" data-id="element-238" data-mp="ozqizdv3xptc">
              REAL WORK. <br data-id="element-239" data-mp="25cnue39zbv5f" />
              <span className="text-[#C8102E]" data-id="element-240" data-mp="25d7d3m7genki">REAL RESULTS.</span>
            </motion.h2>
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-gray-400 max-w-md md:text-right" data-id="element-241" data-mp="219hudddgn07e">
            A look at recent jobs across Monterey County — from construction
            site cleanups to fire damage removal.
          </motion.p>
        </div>

        {/* Main carousel */}
        <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black shadow-2xl" data-id="element-242" data-mp="2t2i8sp9y5rrs">
          {/* Slide window */}
          <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] overflow-hidden" data-id="element-243" data-mp="3cobr89gxdh1p">
            <AnimatePresence custom={direction} mode="popLayout" initial={false} data-id="element-244" data-mp="3hlu05zuxzw9p">
              <motion.div key={current.src} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 280,
                damping: 32
              },
              opacity: {
                duration: 0.25
              }
            }} className="absolute inset-0" drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.2} onDragEnd={(_, info) => {
              if (info.offset.x < -80) next();else if (info.offset.x > 80) prev();
            }} data-id="element-245" data-mp="37o5rs74eyq92">
                <img src={current.src} alt={current.alt} className="w-full h-full object-cover pointer-events-none select-none" draggable={false} data-id="element-246" data-mp="2lgto19nxykhd" />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent pointer-events-none" data-id="element-247" data-mp="2ci88lndrnlkt" />

                {/* Caption */}
                <div className="absolute left-0 right-0 bottom-0 p-6 sm:p-10 pointer-events-none" data-id="element-248" data-mp="22r54ecpjiqhb">
                  <div className="max-w-2xl" data-id="element-249" data-mp="1xt3mhv3uox7f">
                    <span className="inline-block px-3 py-1 rounded-full bg-[#C8102E] text-white text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-3" data-id="element-250" data-mp="1tpnsl6j61ayf">
                      {current.category}
                    </span>
                    <h3 className="text-2xl sm:text-4xl font-bold text-white mb-2 leading-tight" data-id="element-251" data-mp="47qla9gb0k95">
                      {current.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-200 max-w-xl leading-relaxed" data-id="element-252" data-mp="d9gm25w2svjl">
                      {current.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Counter top-right */}
            <div className="absolute top-5 right-5 bg-black/60 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 text-xs font-bold tracking-widest z-10" data-id="element-253" data-mp="3lp9uryk612q3">
              <span className="text-[#C8102E]" data-id="element-254" data-mp="3qn1rd5vr9d0m">
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className="text-gray-400" data-id="element-255" data-mp="2xzp8b5osznc2">
                {' '}
                / {String(jobs.length).padStart(2, '0')}
              </span>
            </div>

            {/* Prev/Next buttons */}
            <button onClick={prev} aria-label="Previous job photo" className="absolute top-1/2 left-3 sm:left-5 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-[#C8102E] backdrop-blur-md border border-white/20 flex items-center justify-center transition-colors z-10 group" data-id="element-256" data-mp="snlbzcnemmou">
              <ChevronLeft className="text-white group-hover:scale-110 transition-transform" size={24} data-id="element-257" data-mp="16muitlpq04g5" />
            </button>
            <button onClick={next} aria-label="Next job photo" className="absolute top-1/2 right-3 sm:right-5 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-[#C8102E] backdrop-blur-md border border-white/20 flex items-center justify-center transition-colors z-10 group" data-id="element-258" data-mp="dxx7k2jxf7ww">
              <ChevronRight className="text-white group-hover:scale-110 transition-transform" size={24} data-id="element-259" data-mp="3mcnzig4eqt17" />
            </button>
          </div>

          {/* Thumbnail strip */}
          <div className="bg-[#141414] border-t border-white/10 p-3 sm:p-4 flex gap-2 sm:gap-3 overflow-x-auto scrollbar-hide" data-id="element-260" data-mp="3ev94tjgkxq7s">
            {jobs.map((job, i) => <button key={job.src} onClick={() => goTo(i)} aria-label={`View ${job.title}`} className={`relative flex-shrink-0 w-20 h-16 sm:w-28 sm:h-20 rounded-md overflow-hidden border-2 transition-all ${i === index ? 'border-[#C8102E] opacity-100 scale-100' : 'border-transparent opacity-60 hover:opacity-100'}`} data-id="element-261" data-mp="39xzfe3t6xejy" data-mpi={i}>
                <img src={job.src} alt="" className="w-full h-full object-cover" draggable={false} data-id="element-262" data-mp="2r3ar6stnrirh" />
                {i === index && <div className="absolute inset-0 bg-[#C8102E]/20 pointer-events-none" data-id="element-263" data-mp="2d5vvx7gq4grl" />}
              </button>)}
          </div>

          {/* Progress dots (mobile) */}
          <div className="absolute bottom-[88px] sm:bottom-[100px] left-1/2 -translate-x-1/2 flex gap-1.5 z-10 sm:hidden" data-id="element-264" data-mp="27jd8cn2iuvi9">
            {jobs.map((_, i) => <span key={i} className={`h-1 rounded-full transition-all ${i === index ? 'w-6 bg-[#C8102E]' : 'w-1.5 bg-white/40'}`} data-id="element-265" data-mp="1tlokd6d5r88z" data-mpi={i} />)}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white/5 border border-white/10 rounded-xl p-6" data-id="element-266" data-mp="2qamfipotl86o">
          <p className="text-gray-300 text-center sm:text-left" data-id="element-267" data-mp="39xtx0wd9etoz">
            <span className="font-bold text-white" data-id="element-268" data-mp="39zeuouuvesr2">
              Your job could be next.
            </span>{' '}
            Free estimates — same-day & next-day availability.
          </p>
          <a href="tel:8312407401" className="inline-flex items-center gap-2 bg-[#C8102E] text-white px-6 py-3 rounded font-bold hover:bg-red-800 transition-colors whitespace-nowrap" data-id="element-269" data-mp="3fasjpnuyjeid">
            <Phone size={18} data-id="element-270" data-mp="3o5yjo67jr2cz" />
            <span data-id="element-271" data-mp="30di0fsc2p4c3">(831) 240-7401</span>
          </a>
        </motion.div>
      </div>
    </section>;
}